import { isProd } from "@/constants/env";
import { TSupplier } from "@/http/types/supplier";
import { TSupplierCommercial } from "@/http/types/supplier-commercials";
import { Experiment, ExperimentClient } from "@amplitude/experiment-js-client";
import * as amplitude from "@amplitude/analytics-browser";

import packageJson from "../../package.json";

let isInitialized = false;
let experimentClient: ExperimentClient | null = null;

const getAmplitudeApiKey = () =>
  isProd ? import.meta.env.VITE_APP_AMPLITUDE_PROD : import.meta.env.VITE_APP_AMPLITUDE_DEV;

const getExperimentClient = () => {
  if (experimentClient) return experimentClient;

  const apiKey = getAmplitudeApiKey();
  if (!apiKey) {
    console.warn("Amplitude Experiment API key not configured");
    return null;
  }

  experimentClient = Experiment.initialize(apiKey);
  return experimentClient;
};

const isExperimentEnabledValue = (value: unknown) => {
  if (value === true || value === 1) return true;
  if (typeof value === "string") {
    const normalized = value.toLowerCase();
    return normalized === "active" || normalized === "enabled" || normalized === "on" || normalized === "true";
  }
  return false;
};

/**
 * Initialize Amplitude with the appropriate API key based on environment
 */
export const initAmplitude = () => {
  if (isInitialized) return;

  const apiKey = getAmplitudeApiKey();

  if (!apiKey) {
    console.warn("Amplitude API key not configured");
    return;
  }

  amplitude.init(apiKey, undefined, {
    appVersion: `${packageJson.version}-${import.meta.env.VITE_APP_BUILD_NUMBER}`,
    autocapture: true,
  });

  isInitialized = true;
};

export const fetchAmplitudeFeatureFlag = async (flagKey: string, userId?: string | null) => {
  const client = getExperimentClient();
  if (!client) return false;

  try {
    await client.fetch(userId ? { user_id: userId } : undefined);
    const variant = client.variant(flagKey);
    return isExperimentEnabledValue(variant?.value);
  } catch (error) {
    console.warn("Failed to fetch Amplitude feature flag", { flagKey, error });
    return false;
  }
};

/**
 * Identify the user in Amplitude with their properties
 * Uses commercial information if available, otherwise uses supplier information
 */
export const identifyUser = (commercial: TSupplierCommercial | null, supplier: TSupplier) => {
  if (!isInitialized) return;

  const user = commercial || supplier;

  // Set user identity
  const userId = commercial?.id;

  if (!userId) return;

  amplitude.setUserId(userId);

  // Set user properties
  const userProperties: Record<string, string> = {
    email: user.email || "",
    firstName: commercial?.firstName || supplier.contactName || "",
    lastName: commercial?.lastName || "",
    supplierName: supplier.name || "",
    supplierEmail: supplier.email || "",
    tenantId: supplier.id || "",
  };

  amplitude.identify(
    new amplitude.Identify()
      .set("id", userId)
      .set("email", userProperties.email)
      .set("firstName", userProperties.firstName)
      .set("lastName", userProperties.lastName)
      .set("supplierName", userProperties.supplierName)
      .set("supplierEmail", userProperties.supplierEmail)
      .set("tenantId", userProperties.tenantId)
  );
};

/**
 * Track user login event
 */
export const trackLogin = () => {
  if (!isInitialized) return;

  amplitude.track("Login");
};

/**
 * Track page view event
 */
export const trackPageView = (
  path: string,
  title?: string,
  commercial?: TSupplierCommercial | null,
  supplier?: TSupplier
) => {
  if (!isInitialized) return;

  // Identify user if they're logged in but not yet identified (for already logged users)
  if (commercial !== undefined && supplier) {
    identifyUser(commercial, supplier);
  }

  const eventProperties: Record<string, string> = {
    path,
    title: title || document.title,
  };

  // Add tenantId if supplier is available
  if (supplier?.id) {
    eventProperties.tenantId = supplier.id;
  }

  amplitude.track("Page Viewed", eventProperties);
};
